import { useState, useEffect } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getQuotations,
  getQuotationPdfTemplates,
  createQuotation,
  getQuotation,
  updateQuotation,
  deleteQuotation,
  getQuotationProducts,
  getQuotationProduct,
  createQuotationProduct,
  updateQuotationProduct,
  deleteQuotationProduct,
  getQuotationActivities,
  downloadQuotationPdf,
  regenerateQuotationPdf,
  getCustomerQuotations,
  getCustomerQuotation,
  createCustomerQuotation,
  sendCustomerQuotation,
  regenerateCustomerQuotationPdf,
  deleteCustomerQuotation,
} from '../api';
import {
  quotationPdfFilename,
  getCachedPdfBlobUrl,
  loadPdfPreviewBlobUrl,
} from '../utils';

function invalidateQuotationQueries(queryClient, { companyId, quotationId }) {
  queryClient.invalidateQueries({
    queryKey: ['quotations', companyId],
  });
  queryClient.invalidateQueries({
    queryKey: ['quotations-infinite', companyId],
  });
  if (quotationId != null) {
    queryClient.invalidateQueries({
      queryKey: ['quotation', companyId, quotationId],
    });
    queryClient.invalidateQueries({
      queryKey: ['quotation-products', companyId, quotationId],
    });
    queryClient.invalidateQueries({
      queryKey: ['quotation-activities', companyId, quotationId],
    });
  }
}

/** After product mutate: product payloads omit PDF fields — refetch quotation. */
async function refreshQuotationAfterProductChange(
  queryClient,
  { companyId, quotationId },
) {
  invalidateQuotationQueries(queryClient, { companyId, quotationId });
  if (!companyId || quotationId == null) return null;
  const fresh = await getQuotation({ companyId, quotationId });
  queryClient.setQueryData(['quotation', companyId, quotationId], fresh);
  return fresh;
}

export function useQuotations(companyId, params = {}, options = {}) {
  return useQuery({
    queryKey: ['quotations', companyId, params],
    queryFn: () => getQuotations(companyId, params),
    enabled: !!companyId,
    ...options,
  });
}

/** Infinite paginated quotations with dynamic search and scroll pagination */
export function useInfiniteQuotations(companyId, params = {}, options = {}) {
  return useInfiniteQuery({
    queryKey: ['quotations-infinite', companyId, params],
    queryFn: ({ pageParam = 1 }) =>
      getQuotations(companyId, { ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (!lastPage?.next) return undefined;
      try {
        const url = new URL(lastPage.next);
        const p = url.searchParams.get('page');
        return p ? parseInt(p, 10) : undefined;
      } catch {
        const match = String(lastPage.next).match(/[?&]page=(\d+)/);
        return match ? parseInt(match[1], 10) : undefined;
      }
    },
    initialPageParam: 1,
    enabled: !!companyId,
    ...options,
  });
}

/** GET .../quotation-templates/ — PDF design catalog (dynamic, no FE hardcode). */
export function useQuotationPdfTemplates(companyId, params = {}, options = {}) {
  return useQuery({
    queryKey: ['quotation-pdf-templates', companyId, params],
    queryFn: () => getQuotationPdfTemplates(companyId, params),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Streams sample PDF for a design template with persistent session caching
 * and priority concurrency queue to prevent backend overload.
 */
export function useQuotationTemplatePreviewPdf(
  companyId,
  templateKey,
  options = {},
) {
  const { enabled = true, priority = 0 } = options;
  const [blobUrl, setBlobUrl] = useState(() =>
    getCachedPdfBlobUrl(companyId, templateKey),
  );
  const [isLoading, setIsLoading] = useState(
    () =>
      !getCachedPdfBlobUrl(companyId, templateKey) &&
      Boolean(enabled && companyId && templateKey),
  );
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!companyId || !templateKey || !enabled) return;

    const cached = getCachedPdfBlobUrl(companyId, templateKey);
    if (cached) {
      setBlobUrl(cached);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setIsError(false);

    loadPdfPreviewBlobUrl(companyId, templateKey, priority)
      .then((url) => {
        if (isMounted) {
          setBlobUrl(url);
          setIsLoading(false);
          setIsError(false);
        }
      })
      .catch((err) => {
        console.warn(
          `[useQuotationTemplatePreviewPdf] Failed for ${templateKey}:`,
          err,
        );
        if (isMounted) {
          setIsError(true);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [companyId, templateKey, enabled, priority]);

  return {
    blobUrl,
    isLoading,
    isError,
    refetch: () => {
      setIsLoading(true);
      setIsError(false);
      return loadPdfPreviewBlobUrl(companyId, templateKey, 25)
        .then((url) => {
          setBlobUrl(url);
          setIsLoading(false);
          setIsError(false);
          return url;
        })
        .catch((err) => {
          setIsError(true);
          setIsLoading(false);
          throw err;
        });
    },
  };
}

export function useQuotation({ companyId, quotationId }, options = {}) {
  return useQuery({
    queryKey: ['quotation', companyId, quotationId],
    queryFn: () => getQuotation({ companyId, quotationId }),
    enabled: !!companyId && !!quotationId,
    ...options,
  });
}

export function useCreateQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createQuotation,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['quotations', variables.companyId],
      });
      if (data?.id) {
        queryClient.setQueryData(
          ['quotation', variables.companyId, data.id],
          data,
        );
      }
    },
  });
}

export function useUpdateQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateQuotation,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['quotations', variables.companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['quotation', variables.companyId, variables.quotationId],
      });
      if (data?.id) {
        queryClient.setQueryData(
          ['quotation', variables.companyId, data.id],
          data,
        );
      }
    },
  });
}

export function useDeleteQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteQuotation,
    onSuccess: (_, variables) => {
      queryClient.removeQueries({
        queryKey: ['quotation', variables.companyId, variables.quotationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['quotations', variables.companyId],
      });
    },
  });
}

export function useQuotationProducts(
  { companyId, quotationId, params = {} },
  options = {},
) {
  return useQuery({
    queryKey: ['quotation-products', companyId, quotationId, params],
    queryFn: () => getQuotationProducts({ companyId, quotationId, params }),
    enabled: !!companyId && !!quotationId,
    ...options,
  });
}

export function useQuotationProduct(
  { companyId, quotationId, productId },
  options = {},
) {
  return useQuery({
    queryKey: ['quotation-product', companyId, quotationId, productId],
    queryFn: () => getQuotationProduct({ companyId, quotationId, productId }),
    enabled: !!companyId && !!quotationId && !!productId,
    ...options,
  });
}

export function useCreateQuotationProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createQuotationProduct,
    onSuccess: async (_, variables) => {
      await refreshQuotationAfterProductChange(queryClient, variables);
    },
  });
}

export function useUpdateQuotationProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateQuotationProduct,
    onSuccess: async (_, variables) => {
      await refreshQuotationAfterProductChange(queryClient, variables);
    },
  });
}

export function useDeleteQuotationProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteQuotationProduct,
    onSuccess: async (_, variables) => {
      await refreshQuotationAfterProductChange(queryClient, variables);
    },
  });
}

/**
 * Quotation / product activity feed.
 * Pass productId to scope history to one product.
 */
export function useQuotationActivities(
  { companyId, quotationId, productId, actionType, pageSize = 20 } = {},
  options = {},
) {
  const params = {
    page_size: pageSize,
    ...(productId != null && productId !== '' ? { product_id: productId } : {}),
    ...(actionType ? { action_type: actionType } : {}),
  };

  return useInfiniteQuery({
    queryKey: [
      'quotation-activities',
      companyId,
      quotationId,
      productId ?? null,
      actionType ?? null,
      pageSize,
    ],
    queryFn: ({ pageParam = 1 }) =>
      getQuotationActivities({
        companyId,
        quotationId,
        params: { ...params, page: pageParam },
      }),
    getNextPageParam: (lastPage) => {
      if (!lastPage?.next) return undefined;
      try {
        const url = new URL(lastPage.next, window.location.origin);
        const page = url.searchParams.get('page');
        return page ? Number(page) : undefined;
      } catch {
        return undefined;
      }
    },
    enabled: !!companyId && !!quotationId,
    ...options,
  });
}

function triggerBlobDownload(blob, filename) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
}

function openBlobInNewTab(blob) {
  const blobUrl = window.URL.createObjectURL(blob);
  const opened = window.open(blobUrl, '_blank', 'noopener,noreferrer');
  // Some browsers return null when noopener is used, even if successful.
  if (!opened) {
    console.warn(
      'Popup may have been blocked or browser returned null due to noopener.',
    );
  }
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
}

/** GET .../pdf/ as blob and open in a new tab. */
export function useOpenQuotationPdf() {
  return useMutation({
    mutationFn: async ({ companyId, quotationId }) => {
      const blob = await downloadQuotationPdf({ companyId, quotationId });
      openBlobInNewTab(blob);
      return blob;
    },
  });
}

/** GET .../pdf/ as blob and save to disk. */
export function useDownloadQuotationPdf() {
  return useMutation({
    mutationFn: async ({ companyId, quotationId, filename }) => {
      const blob = await downloadQuotationPdf({ companyId, quotationId });
      triggerBlobDownload(
        blob,
        filename || quotationPdfFilename({ id: quotationId }),
      );
      return blob;
    },
  });
}

/** POST .../pdf/regenerate/ then cache fresh quotation (pdf_ready / pdf_url). */
export function useRegenerateQuotationPdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: regenerateQuotationPdf,
    onSuccess: async (data, variables) => {
      if (data?.id) {
        queryClient.setQueryData(
          ['quotation', variables.companyId, data.id],
          data,
        );
      } else {
        await refreshQuotationAfterProductChange(queryClient, variables);
      }
      queryClient.invalidateQueries({
        queryKey: ['quotations', variables.companyId],
      });
    },
  });
}

export function useCustomerQuotations(companyId, params = {}, options = {}) {
  return useQuery({
    queryKey: ['customer-quotations', companyId, params],
    queryFn: () => getCustomerQuotations(companyId, params),
    enabled: !!companyId,
    ...options,
  });
}

export function useCustomerQuotation({ companyId, customerQuotationId }) {
  return useQuery({
    queryKey: ['customer-quotation', companyId, customerQuotationId],
    queryFn: () => getCustomerQuotation({ companyId, customerQuotationId }),
    enabled: !!companyId && !!customerQuotationId,
  });
}

/** POST .../customer-quotations/ — creates a quotation with items + auto-generates PDF. */
export function useCreateCustomerQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomerQuotation,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['customer-quotations', variables.companyId],
      });
      if (data?.id) {
        queryClient.setQueryData(
          ['customer-quotation', variables.companyId, String(data.id)],
          data,
        );
      }
    },
  });
}

/** POST .../customer-quotations/{id}/send/ — sends PDF to customer via WhatsApp. */
export function useSendCustomerQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendCustomerQuotation,
    onSuccess: (data, variables) => {
      // Update cached detail with new status ('sent') + any updated fields
      if (data?.id) {
        queryClient.setQueryData(
          [
            'customer-quotation',
            variables.companyId,
            String(variables.customerQuotationId),
          ],
          (prev) => (prev ? { ...prev, ...data } : data),
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['customer-quotations', variables.companyId],
      });
    },
  });
}

/** POST .../regenerate-pdf/ — optional { template_key }; merges PDF fields into detail cache. */
export function useRegenerateCustomerQuotationPdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: regenerateCustomerQuotationPdf,
    onSuccess: (data, variables) => {
      const id = String(
        variables.customerQuotationId ?? data?.customer_quotation_id ?? '',
      );
      if (!id) return;

      const patch = {};
      if (data?.pdf_url != null) patch.pdf_url = data.pdf_url;
      if (data?.pdf_generated_at != null) {
        patch.pdf_generated_at = data.pdf_generated_at;
      }
      if (data?.template_key != null) patch.template_key = data.template_key;
      if (data?.quote_number != null) patch.quote_number = data.quote_number;
      // Full quotation object responses (legacy) merge wholesale
      if (data?.id && data?.items) {
        Object.assign(patch, data);
      } else if (data?.subtotal != null) {
        [
          'subtotal',
          'tax_rate',
          'tax_amount',
          'total_amount',
          'currency',
        ].forEach((key) => {
          if (data[key] != null) patch[key] = data[key];
        });
      }

      queryClient.setQueryData(
        ['customer-quotation', variables.companyId, id],
        (prev) => (prev ? { ...prev, ...patch } : { id, ...patch, ...data }),
      );
      queryClient.invalidateQueries({
        queryKey: ['customer-quotations', variables.companyId],
      });
    },
  });
}

/** DELETE .../customer-quotations/{id}/ — removes quotation and invalidates list cache. */
export function useDeleteCustomerQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomerQuotation,
    onSuccess: (_, variables) => {
      queryClient.removeQueries({
        queryKey: [
          'customer-quotation',
          variables.companyId,
          String(variables.customerQuotationId),
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['customer-quotations', variables.companyId],
      });
    },
  });
}

export * from './useQuotationsStateSnapshot';
